/**
 * AdminAIPersonasPage - AI Persona Management Interface
 *
 * Feature: 071-ai-critique-sessions (US2)
 * Lists all AI personas with actions to view, edit, delete, and export.
 */

import { useState, useEffect, useCallback, useRef, DragEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  useAIPersonas,
  useDeleteAIPersona,
  useExportAIPersona,
  useImportAIPersona,
  AIPersona,
  getProviderDisplayName,
} from '@/hooks/useAIPersonas';
import { IdLink } from '@/components/IdLink';
import logger from '@/utils/logger';
import { toast } from 'sonner';
import {
  adminCreateAIPersonaUrl,
  adminAIPersonaDetailUrl,
  adminAIPersonaEditUrl,
} from '@/utils/urls';

/**
 * Download text content as a file
 */
function downloadFile(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function AdminAIPersonasPage() {
  const { data, loading, error, refetch } = useAIPersonas();
  const { deletePersona, loading: deleteLoading } = useDeleteAIPersona();
  const { exportPersona } = useExportAIPersona();
  const { importPersona, loading: importLoading } = useImportAIPersona();

  const [isDraggingImport, setIsDraggingImport] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch personas on mount
  useEffect(() => {
    refetch();
  }, [refetch]);

  const handleExport = async (persona: AIPersona) => {
    try {
      const { markdown, filename } = await exportPersona(persona.globalId);
      downloadFile(markdown, filename);
      toast.success(`Exported ${persona.displayName}`);
    } catch (err) {
      logger.error('Admin', 'Failed to export persona', err);
      toast.error('Failed to export persona');
    }
  };

  const handleDelete = async (persona: AIPersona) => {
    try {
      await deletePersona(persona.globalId);
      toast.success(`Deleted ${persona.displayName}`);
      setDeleteConfirmId(null);
      refetch();
    } catch (err) {
      logger.error('Admin', 'Failed to delete persona', err);
      toast.error('Failed to delete persona');
    }
  };

  /**
   * Process multiple markdown files for import (upsert behavior)
   */
  const processFiles = useCallback(async (files: File[]) => {
    const mdFiles = files.filter(f => f.name.endsWith('.md'));

    if (mdFiles.length === 0) {
      toast.error('No markdown files found. Please select .md files.');
      return;
    }

    let created = 0;
    let updated = 0;
    let failed = 0;

    for (const file of mdFiles) {
      try {
        const content = await file.text();
        const result = await importPersona(content);

        if (result.wasUpdated) {
          updated++;
          logger.info('Admin', `Updated persona from ${file.name}`, { username: result.username });
        } else {
          created++;
          logger.info('Admin', `Created persona from ${file.name}`, { username: result.username });
        }
      } catch (err) {
        failed++;
        logger.error('Admin', `Failed to import ${file.name}`, err);
      }
    }

    // Show summary toast
    if (failed === 0) {
      const parts = [];
      if (created > 0) parts.push(`${created} created`);
      if (updated > 0) parts.push(`${updated} updated`);
      toast.success(`Import complete: ${parts.join(', ')}`);
    } else if (created + updated > 0) {
      toast.warning(`Import partial: ${created + updated} succeeded, ${failed} failed`);
    } else {
      toast.error(`Import failed: ${failed} file${failed !== 1 ? 's' : ''} could not be imported`);
    }

    refetch();
  }, [importPersona, refetch]);

  /**
   * Handle drag events for the import button
   */
  const handleImportDragEnter = useCallback((e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes('Files')) {
      setIsDraggingImport(true);
    }
  }, []);

  const handleImportDragLeave = useCallback((e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingImport(false);
  }, []);

  const handleImportDragOver = useCallback((e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleImportDrop = useCallback((e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingImport(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      processFiles(files);
    }
  }, [processFiles]);

  /**
   * Handle click on import button - open file picker
   */
  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  /**
   * Handle file input change (from file picker)
   */
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      processFiles(files);
    }
    // Reset input so the same file can be selected again
    e.target.value = '';
  }, [processFiles]);

  return (
    <div className="p-8 overflow-auto">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">AI Personas</h1>
            <p className="text-muted mt-1">Manage AI critic personas for automated photo critiques</p>
          </div>
          <div className="flex gap-3">
            {/* Hidden file input for import */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".md"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            {/* Import button - also a drop target */}
            <button
              onClick={handleImportClick}
              onDragEnter={handleImportDragEnter}
              onDragLeave={handleImportDragLeave}
              onDragOver={handleImportDragOver}
              onDrop={handleImportDrop}
              disabled={importLoading}
              className={`
                px-4 py-2 border rounded-md transition-colors
                ${isDraggingImport
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border hover:bg-surface-2'
                }
                ${importLoading ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              {importLoading ? 'Importing...' : isDraggingImport ? 'Drop .md files here' : 'Import from Markdown'}
            </button>
            <Link
              to={adminCreateAIPersonaUrl()}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              Create Persona
            </Link>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-red-500 font-semibold mb-1">Failed to Load AI Personas</h3>
                <p className="text-sm text-muted">
                  {error.message || 'An error occurred while loading AI personas'}
                </p>
              </div>
              <button
                onClick={() => refetch()}
                className="px-4 py-2 border border-border rounded-md hover:bg-surface-2 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && !data && (
          <div className="bg-surface-1 rounded-lg border border-border p-8 text-center">
            <p className="text-muted">Loading AI personas...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && data && data.personas.length === 0 && (
          <div className="bg-surface-1 rounded-lg border border-border p-8 text-center">
            <h2 className="text-xl font-semibold text-foreground mb-2">No AI Personas</h2>
            <p className="text-muted mb-4">
              Create your first AI persona to enable automated photo critiques.
            </p>
            <Link
              to={adminCreateAIPersonaUrl()}
              className="inline-block px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              Create First Persona
            </Link>
          </div>
        )}

        {/* Personas List */}
        {data && data.personas.length > 0 && (
          <div className="bg-surface-1 rounded-lg border border-border overflow-hidden">
            <table className="w-full">
              <thead className="bg-surface-2 border-b border-border">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                    Persona
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                    Provider / Model
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.personas.map((persona) => (
                  <PersonaRow
                    key={persona.globalId}
                    persona={persona}
                    onExport={() => handleExport(persona)}
                    onDelete={() => setDeleteConfirmId(persona.globalId)}
                    isDeleting={deleteConfirmId === persona.globalId && deleteLoading}
                  />
                ))}
              </tbody>
            </table>

            {/* Footer */}
            <div className="bg-surface-2 px-6 py-3 border-t border-border">
              <div className="text-sm text-muted">
                Total: {data.total} persona{data.total !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirmId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-surface-1 rounded-lg border border-border p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-foreground mb-2">Delete AI Persona?</h3>
              <p className="text-muted mb-4">
                This action cannot be undone. The persona and all its critiques will remain, but
                it will no longer be available for new critique sessions.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirmId(null)}
                  disabled={deleteLoading}
                  className="px-4 py-2 border border-border rounded-md hover:bg-surface-2 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const persona = data?.personas.find((p) => p.globalId === deleteConfirmId);
                    if (persona) handleDelete(persona);
                  }}
                  disabled={deleteLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {deleteLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

interface PersonaRowProps {
  persona: AIPersona;
  onExport: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}

function PersonaRow({ persona, onExport, onDelete, isDeleting }: PersonaRowProps) {
  const createdDate = new Date(persona.createdAt).toLocaleDateString();

  return (
    <tr className="hover:bg-surface-2 transition-colors">
      <td className="px-6 py-4 whitespace-nowrap">
        <div>
          <div className="text-sm font-medium text-foreground">{persona.displayName}</div>
          <div className="text-xs text-muted">@{persona.username}</div>
          <div className="text-xs mt-0.5">
            <IdLink globalId={persona.globalId} typeName="User" className="text-xs text-muted" />
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="text-sm text-foreground">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-500">
            {getProviderDisplayName(persona.aiProvider)}
          </span>
        </div>
        <div className="text-xs text-muted mt-1">{persona.aiModel}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-muted">{createdDate}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <div className="flex justify-end gap-2">
          <Link
            to={adminAIPersonaDetailUrl(persona.globalId)}
            className="text-primary hover:text-primary/80 text-sm transition-colors"
          >
            View
          </Link>
          <Link
            to={adminAIPersonaEditUrl(persona.globalId)}
            className="text-primary hover:text-primary/80 text-sm transition-colors"
          >
            Edit
          </Link>
          <button
            onClick={onExport}
            className="text-muted hover:text-foreground text-sm transition-colors"
          >
            Export
          </button>
          <button
            onClick={onDelete}
            disabled={isDeleting}
            className="text-red-500 hover:text-red-600 text-sm transition-colors disabled:opacity-50"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </td>
    </tr>
  );
}
